import { CommonModule } from '@angular/common';
import {
  Component, ChangeDetectionStrategy, Input, ChangeDetectorRef, OnInit, OnDestroy, Output, EventEmitter, OnChanges, SimpleChanges
} from '@angular/core';
import { Subscription, filter, interval, tap } from 'rxjs';

import { PaginationAutoplayTime, PaginationType } from '@design-system/components/molecules/pagination/entities/pagination.types';
import { Icon, IconFillColor } from '@design-system/components/atoms/icon/entities/icon.types';
import { IconDsComponent } from '@design-system/components/atoms/icon/icon.component';

@Component({
  selector: 'app-ds-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    IconDsComponent
  ]
})
export class PaginationDsComponent implements OnInit, OnDestroy, OnChanges {

  @Input() type = PaginationType.Base;
  @Input() defaultPage = 0;
  @Input() seeHover = false;
  @Input() autoplay = false;
  @Input() moveControl = false;
  @Input() pages = 0;

  @Output() pageHasChanged: EventEmitter<{ page: number }> = new EventEmitter<{ page: number }>();

  public readonly prevIcon = Icon.ChevronLeft;
  public readonly nextIcon = Icon.ChevronRight;
  public readonly iconColor = IconFillColor.Light;

  private marker = 1;
  private subscriptions = new Subscription();

  private readonly autoPlay$ = interval(PaginationAutoplayTime);

  constructor(
    private readonly cdr: ChangeDetectorRef
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.defaultPage && changes?.defaultPage?.currentValue) {
      this.marker = changes?.defaultPage?.currentValue;
      this.setMarker(this.marker);
    }
  }

  ngOnInit(): void {
    this.autoPlay();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get pageList(): number[] {
    return this.pages ? new Array(this.pages).fill(null).map((v, index) => (index + 1)) : [];
  }

  get hasPageControls(): boolean {
    return this.pages && this.pageList?.length > 1;
  }

  public pageHasBeenActivated(page: number): boolean {
    return page === this.marker;
  }

  public trackById(index: number): number {
    return index;
  }

  public moveMarker(event: { add: boolean, subtract: boolean }): void {
    if (event.add) {
      if (this.marker === this.pageList?.length) {
        this.marker = 1;
      } else {
        this.marker = this.marker + 1;
      }
    } else if (event.subtract) {
      if (this.marker === 1) {
        this.marker = this.pageList?.length;
      } else {
        this.marker = this.marker - 1;
      }
    }
    this.pageHasChanged.emit({ page: this.marker });
    this.resetAutoPlay();
  }

  public setMarker(page: number): void {
    this.pageHasChanged.emit({ page });
    this.resetAutoPlay();
  }

  public runProgress(page: number): boolean {
    return this.autoplay && this.pageHasBeenActivated(page);
  }

  private autoPlay(): void {
    this.subscriptions.add(
      this.autoPlay$
        .pipe(
          filter(() => this.autoplay),
          tap(() => {
            this.moveMarker({ add: true, subtract: false });
            this.cdr.detectChanges();
          })
        ).subscribe()
    )
  }

  private resetAutoPlay(): void {
    this.subscriptions?.unsubscribe();
    this.subscriptions = new Subscription();
    this.autoPlay();
  }

}
